import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useToast } from '../lib/toast';
import { Spinner } from '../components/Spinner';
import type { Campaign, ShortLink } from '@shared/types';

interface FormState {
  destinationUrl: string;
  customAlias: string;
  title: string;
  description: string;
  tags: string;
  campaignId: string;
  status: 'active' | 'disabled';
  redirectStatus: 301 | 302 | 307 | 308;
  password: string;
  expiresAt: string;
  maxClicks: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  utmContent: string;
}

const empty: FormState = {
  destinationUrl: '',
  customAlias: '',
  title: '',
  description: '',
  tags: '',
  campaignId: '',
  status: 'active',
  redirectStatus: 302,
  password: '',
  expiresAt: '',
  maxClicks: '',
  utmSource: '',
  utmMedium: '',
  utmCampaign: '',
  utmTerm: '',
  utmContent: '',
};

export function NewLinkPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [form, setForm] = useState<FormState>(empty);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ items: Campaign[] }>('/api/campaigns?pageSize=100')
      .then((d) => setCampaigns(d.items))
      .catch(() => {
        /* non-blocking */
      });
  }, []);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const created = await api.post<ShortLink>('/api/links', {
        destinationUrl: form.destinationUrl,
        customAlias: form.customAlias || undefined,
        title: form.title || undefined,
        description: form.description || undefined,
        tags: form.tags
          ? form.tags
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        campaignId: form.campaignId || undefined,
        status: form.status,
        redirectStatus: form.redirectStatus,
        password: form.password || undefined,
        expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : undefined,
        maxClicks: form.maxClicks ? Number(form.maxClicks) : undefined,
        utm: {
          source: form.utmSource || undefined,
          medium: form.utmMedium || undefined,
          campaign: form.utmCampaign || undefined,
          term: form.utmTerm || undefined,
          content: form.utmContent || undefined,
        },
      });
      toast.success('Short link created');
      navigate(`/app/links/${created.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">New short link</h1>
        <p className="text-sm text-ink-500 dark:text-ink-400">
          Configure your destination URL, optional alias, and link controls.
        </p>
      </header>

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-200 px-3 py-2 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-6">
        <section className="card p-5 space-y-4">
          <h2 className="font-semibold">Destination</h2>
          <div>
            <label className="label" htmlFor="destinationUrl">
              Destination URL <span className="text-red-500">*</span>
            </label>
            <input
              id="destinationUrl"
              required
              type="url"
              placeholder="https://example.com/very-long-url"
              className="input"
              value={form.destinationUrl}
              onChange={(e) => update('destinationUrl', e.target.value)}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="customAlias">
                Custom alias (optional)
              </label>
              <input
                id="customAlias"
                className="input"
                placeholder="my-event"
                value={form.customAlias}
                onChange={(e) => update('customAlias', e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="redirectStatus">
                Redirect status
              </label>
              <select
                id="redirectStatus"
                className="input"
                value={form.redirectStatus}
                onChange={(e) =>
                  update('redirectStatus', Number(e.target.value) as 301 | 302 | 307 | 308)
                }
              >
                <option value={302}>302 (default)</option>
                <option value={301}>301 (permanent)</option>
                <option value={307}>307 (temporary)</option>
                <option value={308}>308 (permanent)</option>
              </select>
            </div>
          </div>
        </section>

        <section className="card p-5 space-y-4">
          <h2 className="font-semibold">Metadata</h2>
          <div>
            <label className="label" htmlFor="title">
              Title
            </label>
            <input
              id="title"
              className="input"
              value={form.title}
              onChange={(e) => update('title', e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="description">
              Description
            </label>
            <textarea
              id="description"
              className="input"
              rows={3}
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="tags">
                Tags (comma separated)
              </label>
              <input
                id="tags"
                className="input"
                placeholder="launch, q4, social"
                value={form.tags}
                onChange={(e) => update('tags', e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="campaignId">
                Campaign
              </label>
              <select
                id="campaignId"
                className="input"
                value={form.campaignId}
                onChange={(e) => update('campaignId', e.target.value)}
              >
                <option value="">No campaign</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <section className="card p-5 space-y-4">
          <h2 className="font-semibold">Controls</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="password">
                Password protection (optional)
              </label>
              <input
                id="password"
                type="password"
                className="input"
                value={form.password}
                onChange={(e) => update('password', e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="label" htmlFor="status">
                Status
              </label>
              <select
                id="status"
                className="input"
                value={form.status}
                onChange={(e) =>
                  update('status', e.target.value as 'active' | 'disabled')
                }
              >
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
            <div>
              <label className="label" htmlFor="expiresAt">
                Expires at (UTC)
              </label>
              <input
                id="expiresAt"
                type="datetime-local"
                className="input"
                value={form.expiresAt}
                onChange={(e) => update('expiresAt', e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="maxClicks">
                Maximum clicks
              </label>
              <input
                id="maxClicks"
                type="number"
                min={1}
                className="input"
                value={form.maxClicks}
                onChange={(e) => update('maxClicks', e.target.value)}
              />
            </div>
          </div>
        </section>

        <section className="card p-5 space-y-4">
          <h2 className="font-semibold">UTM parameters</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label">utm_source</label>
              <input
                className="input"
                value={form.utmSource}
                onChange={(e) => update('utmSource', e.target.value)}
              />
            </div>
            <div>
              <label className="label">utm_medium</label>
              <input
                className="input"
                value={form.utmMedium}
                onChange={(e) => update('utmMedium', e.target.value)}
              />
            </div>
            <div>
              <label className="label">utm_campaign</label>
              <input
                className="input"
                value={form.utmCampaign}
                onChange={(e) => update('utmCampaign', e.target.value)}
              />
            </div>
            <div>
              <label className="label">utm_term</label>
              <input
                className="input"
                value={form.utmTerm}
                onChange={(e) => update('utmTerm', e.target.value)}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="label">utm_content</label>
              <input
                className="input"
                value={form.utmContent}
                onChange={(e) => update('utmContent', e.target.value)}
              />
            </div>
          </div>
        </section>

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate(-1)}
            disabled={submitting}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting && <Spinner size={16} />}
            Create link
          </button>
        </div>
      </form>
    </div>
  );
}
